import React, { useState, useEffect } from 'react'
import { Box, Button, Checkbox, Stack, Typography } from '@mui/material'
import { Search, MapPin } from 'lucide-react'
import { DrawerPopup } from 'components/UI/DrawerPopup'
import { Input } from 'components/UI/Input'
import { MENU_ROW_LABEL_SX, menuAccentColor, menuRowToneSx } from 'components/UI/menuRowTone'
import { createApiCall } from 'services'
import { font } from 'settings/tokens';

export type Location = {
  id: string
  location: string
  city?: string
  country?: string
  count: number
}

export type LocationSearchPickerProps = {
  mobile?: boolean
  open: boolean
  onOpen: () => void
  onClose: () => void
  certificateId: string
  selectedLocationIds: string[]
  onToggleLocationId: (id: string, checked: boolean) => void
  onClear: () => void
  onApply: () => void
}

export const LocationSearchPicker = ({
  mobile,
  open,
  onOpen,
  onClose,
  certificateId,
  selectedLocationIds,
  onToggleLocationId,
  onClear,
  onApply,
}: LocationSearchPickerProps) => {
  const [query, setQuery] = useState('')
  const [locations, setLocations] = useState<Location[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (open && certificateId) {
      fetchLocations()
    }
  }, [open, certificateId])

  useEffect(() => {
    if (open) {
      setQuery('')
    }
  }, [open])

  const fetchLocations = async () => {
    setLoading(true)
    try {
      const { data, status } = await createApiCall({
        url: `/scan-log/public/${certificateId}`,
        method: 'GET',
        params: { limit: 1000 }
      })

      if (status === 200 && data?.data) {
        const locationMap = new Map<string, Location>()
        
        data.data.forEach((scan: any) => {
          if (scan.metadata?.location) {
            const locationKey = scan.metadata.location
            if (locationMap.has(locationKey)) {
              locationMap.get(locationKey)!.count++
            } else {
              locationMap.set(locationKey, {
                id: locationKey,
                location: scan.metadata.location,
                city: scan.metadata.city,
                country: scan.metadata.country,
                count: 1
              })
            }
          }
        })

        setLocations(Array.from(locationMap.values()).sort((a, b) => b.count - a.count))
      }
    } catch (error) {
      console.error('Error fetching locations:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredLocations = locations.filter(location =>
    location.location.toLowerCase().includes(query.toLowerCase()) ||
    location.city?.toLowerCase().includes(query.toLowerCase()) ||
    location.country?.toLowerCase().includes(query.toLowerCase())
  )

  const content = (
    <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack sx={{ gap: 2, flex: '1 1 auto', overflowY: 'auto' }}>
        {/* Was a hand-rolled surface with `borderRadius: '12px'` and a `grey[300]`
            border — the exact drift `fieldSurfaceSx` documents itself as existing to
            stop, and the hardcoded 12 bypassed the radius clamp (which resolves to 6).
            The base field carries the clamp, the token border, and the font
            inheritance a bare `<input>` does not get. */}
        <Input
          value={query}
          onChange={setQuery}
          placeholder="Search locations"
          startAdornment={<Search size={16} />}
          inputProps={{ 'aria-label': 'Search locations' }}
        />

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography>Loading locations...</Typography>
          </Box>
        ) : (
          <Stack sx={{ gap: 1 }}>
            {filteredLocations.map((location) => {
              const isSelected = selectedLocationIds.includes(location.id)
              return (
                <Box
                  key={location.id}
                  onClick={() => onToggleLocationId(location.id, !isSelected)}
                  sx={theme => ({
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    ...menuRowToneSx(theme, { selected: isSelected }),
                    // The bug this replaces: selected and hover were BOTH grey[50], so
                    // pointing at a selected row erased the only cue that it WAS
                    // selected — the two states rendered as the same pixels. Selection
                    // now owns a darker resting wash (grey[100], matching
                    // `PickerListItem`'s selected row) that hover does not overwrite;
                    // hover speaks through the accent text instead. Background = chosen,
                    // text = pointing at, so the two never collide again.
                    ...(isSelected && {
                      backgroundColor: theme.palette.grey[100],
                      '&:hover, &:focus-visible': {
                        backgroundColor: theme.palette.grey[100],
                        color: menuAccentColor(theme),
                      },
                    }),
                  })}
                >
                  <Checkbox
                    checked={isSelected}
                    sx={{ p: 0 }}
                    icon={
                      <Box
                        component="span"
                        sx={theme => ({
                          width: 18,
                          height: 18,
                          borderRadius: '6px',
                          border: `1px solid ${theme.palette.text.primary}`,
                          backgroundColor: theme.palette.background.paper,
                          display: 'inline-block',
                        })}
                      />
                    }
                    checkedIcon={
                      <Box
                        component="span"
                        sx={theme => ({
                          width: 18,
                          height: 18,
                          borderRadius: '6px',
                          border: `1px solid ${theme.palette.primary.main}`,
                          backgroundColor: theme.palette.primary.main,
                          display: 'inline-block',
                          position: 'relative',
                          '&::after': {
                            content: '"✓"',
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: 'white',
                            fontSize: font.label,
                            fontWeight: 'bold'
                          }
                        })}
                      />
                    }
                  />
                  <MapPin size={16} color="currentColor" />
                  <Stack sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ ...MENU_ROW_LABEL_SX, fontSize: 14, fontWeight: 500 }}>
                      {location.location}
                    </Typography>
                    {/* Genuinely secondary — the scan count stays `text.secondary` at
                        every row state. It is NOT swept into the row's inherit. */}
                    <Typography sx={{ fontSize: font.label, color: 'text.secondary' }}>
                      {location.count} scan{location.count !== 1 ? 's' : ''}
                    </Typography>
                  </Stack>
                </Box>
              )
            })}
          </Stack>
        )}
      </Stack>

      <Box sx={{ p: 2, borderTop: theme => `1px solid ${theme.palette.divider}`, backgroundColor: theme => theme.palette.background.paper }}>
        <Button
          variant="outlined"
          fullWidth
          onClick={onClear}
          sx={{ borderRadius: '12px' }}
        >
          Clear all
        </Button>
      </Box>
    </Stack>
  )

  return (
    <DrawerPopup
      title="Activity location"
      rightActionLabel={mobile ? 'Done' : undefined}
      rightActionOnClick={mobile ? onApply : undefined}
      anchor={mobile ? 'bottom' : 'right'}
      child={content}
      handleOpen={open}
      handleClose={onClose}
      paperSx={mobile ? { height: '100vh' } : { width: 400, maxWidth: '80vw', mr: 2, my: 2, borderRadius: '32px', height: 'calc(100% - 32px)' }}
    />
  )
}

export default LocationSearchPicker
